import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { PendingApprovalsAlert } from '../components/PendingApprovalsAlert';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { 
  Store, 
  Users, 
  AlertTriangle, 
  TrendingUp, 
  Banknote,
  CreditCard,
  PoundSterling,
  ArrowUpRight,
  ArrowDownRight,
  Fuel,
  RefreshCw,
  Building,
  Shield,
  Landmark,
  Package,
  Receipt,
  Calculator,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(value || 0);
};

export const DashboardPage = () => {
  const { user, isAdmin, isSuperUser } = useAuth();
  const { stores } = useStore();
  
  // Check if user has access to fuel stations
  const userStoreIds = user?.assigned_stores || [];
  const userStores = stores.filter(s => userStoreIds.includes(s.id));
  const hasFuelStations = isSuperUser || userStores.some(s => s.store_type === 'fuel_station');
  const [loading, setLoading] = useState(true);
  const [selectedStoreFilter, setSelectedStoreFilter] = useState('all');
  
  // All Stores View Data
  const [allStoresData, setAllStoresData] = useState([]);
  
  // Single Store View Data
  const [storeOverview, setStoreOverview] = useState(null);
  const [storeLoans, setStoreLoans] = useState([]);
  
  // Legacy dashboard data for summary
  const [dashboardData, setDashboardData] = useState(null);
  
  // Staff-specific data
  const [lastSubmissions, setLastSubmissions] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Check if user is staff
  const isStaff = user?.user_type === 'staff';

  // Update time every minute for staff dashboard
  useEffect(() => {
    if (isStaff) {
      const timer = setInterval(() => setCurrentTime(new Date()), 60000);
      return () => clearInterval(timer);
    }
  }, [isStaff]);

  useEffect(() => {
    if (isStaff && stores.length > 0) {
      fetchStaffDashboard();
    } else if (!isStaff && selectedStoreFilter === 'all') {
      fetchAllStoresOverview();
    } else if (!isStaff && selectedStoreFilter !== 'all') {
      fetchStoreOverview(selectedStoreFilter);
    }
  }, [selectedStoreFilter, isStaff, stores]);

  const fetchStaffDashboard = async () => {
    setLoading(true);
    try {
      // Fetch last submissions for staff's assigned stores
      const today = new Date().toISOString().split('T')[0];
      const submissions = [];
      
      for (const store of stores) {
        try {
          const res = await axios.get(`${API}/sales`, {
            params: { store_id: store.id, date: today }
          });
          if (res.data && res.data.length > 0) {
            submissions.push({
              store_name: store.name,
              store_id: store.id,
              ...res.data[0]
            });
          } else {
            submissions.push({
              store_name: store.name,
              store_id: store.id,
              not_submitted: true
            });
          }
        } catch (err) {
          submissions.push({
            store_name: store.name,
            store_id: store.id,
            not_submitted: true
          });
        }
      }
      setLastSubmissions(submissions);
    } catch (error) {
      console.error('Failed to fetch staff dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStoresOverview = async () => {
    setLoading(true);
    try {
      const [overviewRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/reports/all-stores-overview`),
        axios.get(`${API}/reports/dashboard`)
      ]);
      setAllStoresData(overviewRes.data);
      setDashboardData(dashboardRes.data);
      setStoreOverview(null);
    } catch (error) {
      console.error('Failed to fetch all stores overview:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreOverview = async (storeId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/store-overview/${storeId}`);
      setStoreOverview(response.data);
      setAllStoresData([]);
      
      // Fetch loans for this store
      try {
        const loansRes = await axios.get(`${API}/loans/store/${storeId}`);
        setStoreLoans(loansRes.data || []);
      } catch (e) {
        setStoreLoans([]);
      }
    } catch (error) {
      console.error('Failed to fetch store overview:', error);
      toast.error('Failed to load store data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for All Stores view
  const getTotals = () => {
    if (!allStoresData.length) return {};
    return {
      totalCash: allStoresData.reduce((sum, s) => sum + (s.cash_holding || 0), 0),
      totalSafe: allStoresData.reduce((sum, s) => sum + (s.safe_balance || 0), 0),
      totalBank: allStoresData.reduce((sum, s) => sum + (s.bank_balance || 0), 0),
      totalCreditors: allStoresData.reduce((sum, s) => sum + (s.total_creditors || 0), 0),
      totalInvestments: allStoresData.reduce((sum, s) => sum + (s.total_investments || 0), 0),
      totalVouchers: allStoresData.reduce((sum, s) => sum + (s.voucher_balance || 0), 0),
      yesterdaySales: allStoresData.reduce((sum, s) => sum + (s.yesterday_sales || 0), 0),
      yesterdayShopSales: allStoresData.reduce((sum, s) => sum + (s.yesterday_shop_sales || 0), 0),
      yesterdayFuelVolume: allStoresData.reduce((sum, s) => sum + (s.yesterday_fuel_volume || 0), 0),
      currentWeekSales: allStoresData.reduce((sum, s) => sum + (s.current_week_sales || 0), 0),
      lastWeekSales: allStoresData.reduce((sum, s) => sum + (s.last_week_sales || 0), 0),
      totalFuelCardBalance: allStoresData.reduce((sum, s) => sum + (s.fuel_card_balance || 0), 0)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  const totals = getTotals();

  // Staff Dashboard View
  if (isStaff) {
    return (
      <div className="space-y-6" data-testid="staff-dashboard-page">
        {/* Welcome Header */}
        <div className="text-center py-8">
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            Welcome, {user?.name || 'Staff'}
          </h1>
          <p className="text-2xl text-muted-foreground">
            {currentTime.toLocaleDateString('en-GB', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}
          </p>
          <p className="text-xl text-primary mt-1">
            {currentTime.toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit'
            })}
          </p>
        </div>

        {/* Assigned Stores & Today's Submissions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Your Assigned Stores - Today's Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {loading ? (
                <p className="text-muted-foreground text-center py-8">Loading stores...</p>
              ) : lastSubmissions.length === 0 && stores.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No stores assigned to your account</p>
              ) : lastSubmissions.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Loading submissions...</p>
              ) : (
                lastSubmissions.map((submission, index) => (
                  <div 
                    key={submission.store_id} 
                    className={`p-4 rounded-lg border ${
                      submission.not_submitted 
                        ? 'border-yellow-500/50 bg-yellow-500/10' 
                        : 'border-green-500/50 bg-green-500/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          submission.not_submitted ? 'bg-yellow-500' : 'bg-green-500'
                        }`} />
                        <div>
                          <h3 className="font-semibold text-lg">{submission.store_name}</h3>
                          {submission.not_submitted ? (
                            <p className="text-yellow-500 text-sm">No submission for today</p>
                          ) : (
                            <div className="text-sm text-muted-foreground">
                              <p>
                                Sales entered for{' '}
                                <span className="text-foreground font-medium">
                                  {submission.sales_date 
                                    ? new Date(submission.sales_date + 'T00:00:00').toLocaleDateString('en-GB', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                      })
                                    : 'Today'}
                                </span>
                              </p>
                              <p className="mt-1">
                                <span className="text-green-500 font-medium">Submitted</span>
                                {submission.submitted_at && (
                                  <span> on {new Date(submission.submitted_at).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                  })} at {new Date(submission.submitted_at).toLocaleTimeString('en-GB', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}</span>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {!submission.not_submitted && !isStaff && (
                          <>
                            <p className="text-xs text-muted-foreground">Total Sales</p>
                            <p className="text-lg font-bold font-mono">
                              {formatCurrency(submission.total_sales_amount)}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Button 
                variant="outline" 
                className="h-20 flex flex-col gap-2"
                onClick={() => window.location.href = '/sales-entry'}
              >
                <Receipt className="h-6 w-6" />
                <span>Sales Entry</span>
              </Button>
              {stores.some(s => s.store_type === 'fuel_station') && (
                <Button 
                  variant="outline" 
                  className="h-20 flex flex-col gap-2"
                  onClick={() => window.location.href = '/fuel'}
                >
                  <Fuel className="h-6 w-6" />
                  <span>Fuel Management</span>
                </Button>
              )}
              <Button 
                variant="outline" 
                className="h-20 flex flex-col gap-2"
                onClick={() => window.location.href = '/purchases'}
              >
                <Package className="h-6 w-6" />
                <span>Purchases</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {(isAdmin || isSuperUser) && <PendingApprovalsAlert />}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            {selectedStoreFilter === 'all' 
              ? 'Overview of all stores' 
              : `Detailed view: ${storeOverview?.store_name || ''}`}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={selectedStoreFilter} onValueChange={setSelectedStoreFilter}>
            <SelectTrigger className="w-[200px]" data-testid="dashboard-store-filter">
              <SelectValue placeholder="Select store" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button 
            variant="outline" 
            size="icon"
            onClick={() => selectedStoreFilter === 'all' ? fetchAllStoresOverview() : fetchStoreOverview(selectedStoreFilter)}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ============ ALL STORES VIEW ============ */}
      {selectedStoreFilter === 'all' && (
        <>
          {/* Overall Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="card-hover border-green-500/20">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Total Cash Holding</p>
                <p className="text-xl font-bold font-mono text-green-400">
                  {formatCurrency(totals.totalCash)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover border-amber-500/20">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Total Safe Balance</p>
                <p className="text-xl font-bold font-mono text-amber-400">
                  {formatCurrency(totals.totalSafe)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover border-blue-500/20">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Total Bank Balance</p>
                <p className="text-xl font-bold font-mono text-blue-400">
                  {formatCurrency(totals.totalBank)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover border-red-500/20">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Total Creditors</p>
                <p className="text-xl font-bold font-mono text-red-400">
                  {totals.totalCreditors > 0 ? '-' : ''}{formatCurrency(totals.totalCreditors)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover border-green-500/20">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Total Investments</p>
                <p className="text-xl font-bold font-mono text-green-400">
                  {formatCurrency(totals.totalInvestments)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover border-orange-500/20">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Fuel Card Balance</p>
                <p className="text-xl font-bold font-mono text-orange-400">
                  {formatCurrency(totals.totalFuelCardBalance)}
                </p>
              </CardContent>
            </Card>
            {totals.totalVouchers > 0 && (
              <Card className="card-hover border-pink-500/20">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Total Voucher Balance</p>
                  <p className="text-xl font-bold font-mono text-pink-400">
                    {formatCurrency(totals.totalVouchers)}
                  </p>
                </CardContent>
              </Card>
            )}
            <Card className="card-hover">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Yesterday Sales</p>
                <p className="text-xl font-bold font-mono">
                  {formatCurrency(totals.yesterdaySales)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Store by Store Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>
              Store Overview
            </h2>
            
            {allStoresData.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Store className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No stores found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {allStoresData.map((store) => (
                  <Card 
                    key={store.store_id} 
                    className="card-hover cursor-pointer"
                    onClick={() => setSelectedStoreFilter(store.store_id)}
                    data-testid={`store-card-${store.store_id}`}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Store className="h-5 w-5 text-primary" />
                          {store.store_name}
                        </span>
                        <span className="text-xs bg-muted px-2 py-1 rounded text-muted-foreground">
                          {store.store_type?.replace('_', ' ')}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Balances Row */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                          <p className="text-xs text-muted-foreground">Cash</p>
                          <p className="font-mono font-bold text-green-400">
                            {formatCurrency(store.cash_holding)}
                          </p>
                        </div>
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          <p className="text-xs text-muted-foreground">Safe</p>
                          <p className="font-mono font-bold text-amber-400">
                            {formatCurrency(store.safe_balance)}
                          </p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                          <p className="text-xs text-muted-foreground">Bank</p>
                          <p className="font-mono font-bold text-blue-400">
                            {formatCurrency(store.bank_balance)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Sales Row */}
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Yesterday</p>
                          {store.store_type === 'fuel_station' ? (
                            <>
                              <p className="font-mono text-xs">Shop: {formatCurrency(store.yesterday_shop_sales)}</p>
                              <p className="font-mono text-xs text-orange-400">Fuel: {(store.yesterday_fuel_volume || 0).toLocaleString()} L</p>
                            </>
                          ) : (
                            <p className="font-mono">{formatCurrency(store.yesterday_sales)}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">This Week</p>
                          <p className="font-mono">{formatCurrency(store.current_week_sales)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Last Week</p>
                          <p className="font-mono">{formatCurrency(store.last_week_sales)}</p>
                        </div>
                      </div>

                      {/* Creditors, Investments & Card Balance */}
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-red-400" />
                          <span className="text-muted-foreground">Creditors:</span>
                          <span className="font-mono text-red-400">
                            {store.total_creditors > 0 ? '-' : ''}{formatCurrency(store.total_creditors)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Landmark className="h-4 w-4 text-green-400" />
                          <span className="text-muted-foreground">Investments:</span>
                          <span className="font-mono text-green-400">
                            {formatCurrency(store.total_investments)}
                          </span>
                        </div>
                        {store.voucher_balance > 0 && (
                          <div className="flex items-center gap-2">
                            <Gift className="h-4 w-4 text-pink-400" />
                            <span className="text-muted-foreground">Vouchers:</span>
                            <span className="font-mono text-pink-400">
                              {formatCurrency(store.voucher_balance)}
                            </span>
                          </div>
                        )}
                        {hasFuelStations && store.fuel_card_balance > 0 && (
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-orange-400" />
                            <span className="text-muted-foreground">Cards:</span>
                            <span className="font-mono text-orange-400">
                              {formatCurrency(store.fuel_card_balance)}
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ============ SINGLE STORE VIEW ============ */}
      {selectedStoreFilter !== 'all' && storeOverview && (
        <>
          {/* Key Metrics Row 1 - Balances */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/30">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-green-500/20">
                    <Banknote className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Cash Holding</p>
                    <p className="text-2xl font-bold font-mono text-green-400">
                      {formatCurrency(storeOverview.cash_holding)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-amber-500/20">
                    <Shield className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Safe Balance</p>
                    <p className="text-2xl font-bold font-mono text-amber-400">
                      {formatCurrency(storeOverview.safe_balance)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-500/10 to-transparent border-red-500/30">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-red-500/20">
                    <Users className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Creditors</p>
                    <p className="text-2xl font-bold font-mono text-red-400">
                      {storeOverview.total_creditors > 0 ? '-' : ''}{formatCurrency(storeOverview.total_creditors)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/30">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-purple-500/20">
                    <Landmark className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Investments</p>
                    <p className="text-2xl font-bold font-mono text-green-400">
                      {formatCurrency(storeOverview.total_investments)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Fuel Card Balances (if store has outstanding balances and user has fuel station access) */}
          {hasFuelStations && storeOverview.fuel_card_balances && storeOverview.fuel_card_balances.total > 0 && (
            <Card className="border-orange-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="h-5 w-5 text-orange-500" />
                  Outstanding Card Balances
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {storeOverview.fuel_card_balances.non_bunkering > 0 && (
                    <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <p className="text-xs text-muted-foreground">Non Bunkering</p>
                      <p className="font-mono font-bold text-blue-400">
                        {formatCurrency(storeOverview.fuel_card_balances.non_bunkering)}
                      </p>
                    </div>
                  )}
                  {storeOverview.fuel_card_balances.bunkering > 0 && (
                    <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                      <p className="text-xs text-muted-foreground">Bunkering</p>
                      <p className="font-mono font-bold text-orange-400">
                        {formatCurrency(storeOverview.fuel_card_balances.bunkering)}
                      </p>
                    </div>
                  )}
                  {storeOverview.fuel_card_balances.key_fuels > 0 && (
                    <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                      <p className="text-xs text-muted-foreground">Key Fuels</p>
                      <p className="font-mono font-bold text-green-400">
                        {formatCurrency(storeOverview.fuel_card_balances.key_fuels)}
                      </p>
                    </div>
                  )}
                  {storeOverview.fuel_card_balances.amex > 0 && (
                    <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                      <p className="text-xs text-muted-foreground">AMEX</p>
                      <p className="font-mono font-bold text-purple-400">
                        {formatCurrency(storeOverview.fuel_card_balances.amex)}
                      </p>
                    </div>
                  )}
                  {storeOverview.fuel_card_balances.voucher > 0 && (
                    <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                      <p className="text-xs text-muted-foreground">Voucher</p>
                      <p className="font-mono font-bold text-emerald-400">
                        {formatCurrency(storeOverview.fuel_card_balances.voucher)}
                      </p>
                    </div>
                  )}
                  <div className="p-3 bg-muted/50 rounded-lg border">
                    <p className="text-xs text-muted-foreground">Total Outstanding</p>
                    <p className="font-mono font-bold text-lg">
                      {formatCurrency(storeOverview.fuel_card_balances.total)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Banking Details (Bank Accounts and Credit Cards) */}
          {((storeOverview.bank_accounts && storeOverview.bank_accounts.length > 0) || 
            (storeOverview.credit_cards && storeOverview.credit_cards.length > 0)) && (
            <Card className="border-cyan-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Landmark className="h-5 w-5 text-cyan-500" />
                  Banking Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {/* Bank Accounts */}
                  {storeOverview.bank_accounts && storeOverview.bank_accounts.map((account, idx) => (
                    <div key={`bank-${idx}`} className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20 flex flex-col min-h-[100px]">
                      <div className="flex items-center gap-2 mb-2">
                        <Building className="h-4 w-4 text-blue-400" />
                        <p className="font-medium text-sm truncate">{account.name || 'Bank Account'}</p>
                      </div>
                      <div className="flex-1" />
                      <p className="font-mono font-bold text-blue-400 text-lg">
                        {formatCurrency(account.balance || 0)}
                      </p>
                    </div>
                  ))}

                  {/* Credit Cards */}
                  {storeOverview.credit_cards && storeOverview.credit_cards.map((card, idx) => {
                    const limit = card.credit_limit || card.limit || 0;
                    const used = card.balance || 0;
                    const available = limit - used;
                    return (
                      <div key={`card-${idx}`} className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20 flex flex-col min-h-[100px]">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="h-4 w-4 text-purple-400" />
                          <p className="font-medium text-sm truncate">{card.name || 'Credit Card'}</p>
                        </div>
                        <div className="flex-1" />
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">Used:</span>
                            <span className="font-mono text-sm text-red-400">
                              -{formatCurrency(used)}
                            </span>
                          </div>
                          {limit > 0 && (
                            <>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-foreground">Limit:</span>
                                <span className="font-mono text-xs text-muted-foreground">
                                  {formatCurrency(limit)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center pt-1 border-t border-purple-500/20">
                                <span className="text-xs text-muted-foreground">Available:</span>
                                <span className={`font-mono text-sm font-bold ${available >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {formatCurrency(available)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Active Loans Section */}
          {storeLoans && storeLoans.length > 0 && (
            <Card className="border-amber-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Landmark className="h-4 w-4 text-amber-500" />
                  Active Loans ({storeLoans.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {storeLoans.map((loan, idx) => (
                    <div 
                      key={loan.id || idx} 
                      className={`p-3 rounded-lg border ${loan.is_overdue ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/20'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Landmark className={`h-4 w-4 ${loan.is_overdue ? 'text-red-400' : 'text-amber-400'}`} />
                          <div>
                            <p className="font-medium text-sm">{loan.lender}</p>
                            <p className="text-xs text-muted-foreground">{loan.loan_type}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-red-400">
                            -{formatCurrency(loan.current_balance)}
                          </p>
                          {loan.next_due_date && (
                            <p className={`text-xs ${loan.is_overdue ? 'text-red-400 font-semibold' : 'text-muted-foreground'}`}>
                              {loan.is_overdue ? 'OVERDUE: ' : 'Due: '}
                              {new Date(loan.next_due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                              {' • '}{formatCurrency(loan.monthly_payment)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 border-t flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Outstanding:</span>
                    <span className="font-mono font-bold text-red-400">
                      -{formatCurrency(storeLoans.reduce((sum, l) => sum + (l.current_balance || 0), 0))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Voucher Balance Section */}
          {storeOverview.voucher_enabled && (storeOverview.voucher_balance > 0 || storeOverview.voucher_pending > 0) && (
            <Card className="border-pink-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Gift className="h-4 w-4 text-pink-500" />
                  Voucher Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-pink-500/10 rounded-lg">
                      <Gift className="h-6 w-6 text-pink-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Available Balance</p>
                      <p className="text-2xl font-mono font-bold text-pink-500">
                        {formatCurrency(storeOverview.voucher_balance || 0)}
                      </p>
                    </div>
                  </div>
                  {storeOverview.voucher_pending > 0 && (
                    <div className="text-right p-3 bg-amber-500/10 rounded-lg">
                      <p className="text-xs text-muted-foreground">Pending Returns</p>
                      <p className="font-mono font-bold text-amber-500">
                        {formatCurrency(storeOverview.voucher_pending)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sales Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Yesterday's Sales</p>
                {storeOverview.store_type === 'fuel_station' ? (
                  <div className="mt-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Shop:</span>
                      <span className="text-lg font-bold font-mono">{formatCurrency(storeOverview.yesterday_shop_sales)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-orange-400">
                      <Fuel className="h-4 w-4" />
                      <span className="text-xs">Fuel:</span>
                      <span className="text-lg font-bold font-mono">{(storeOverview.yesterday_fuel_volume || 0).toLocaleString()} L</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-2xl font-bold font-mono mt-1">
                    {formatCurrency(storeOverview.yesterday_sales)}
                  </p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Current Week Sales</p>
                <p className="text-2xl font-bold font-mono mt-1">
                  {formatCurrency(storeOverview.current_week_sales)}
                </p>
                {storeOverview.last_week_sales > 0 && (
                  <div className={`flex items-center mt-2 text-sm ${
                    storeOverview.current_week_sales >= storeOverview.last_week_sales 
                      ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {storeOverview.current_week_sales >= storeOverview.last_week_sales 
                      ? <ArrowUpRight className="h-4 w-4" /> 
                      : <ArrowDownRight className="h-4 w-4" />}
                    <span>
                      {Math.abs(((storeOverview.current_week_sales - storeOverview.last_week_sales) / storeOverview.last_week_sales * 100)).toFixed(1)}% vs last week
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Last Week Sales</p>
                <p className="text-2xl font-bold font-mono mt-1">
                  {formatCurrency(storeOverview.last_week_sales)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Last Week Performance Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-hover">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Receipt className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Last Week Purchases</p>
                </div>
                <p className="text-xl font-bold font-mono">
                  {formatCurrency(storeOverview.last_week_purchases)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Last Week Profit</p>
                </div>
                <p className={`text-xl font-bold font-mono ${
                  storeOverview.last_week_profit >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {formatCurrency(storeOverview.last_week_profit)}
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Last Week Margin</p>
                </div>
                <p className={`text-xl font-bold font-mono ${
                  storeOverview.last_week_margin >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {storeOverview.last_week_margin.toFixed(2)}%
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Total Till Variance</p>
                </div>
                <p className={`text-xl font-bold font-mono ${
                  Math.abs(storeOverview.total_till_variance) <= 5 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {formatCurrency(storeOverview.total_till_variance)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Scratch Card Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Scratch Card Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Gift className="h-5 w-5 text-primary" />
                  Scratch Card Stock
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stock breakdown table */}
                {storeOverview.scratch_stock_items?.length > 0 ? (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-muted/30 border-b border-border">
                          <th className="text-left p-3 text-sm font-medium text-muted-foreground">Item</th>
                          <th className="text-right p-3 text-sm font-medium text-muted-foreground">Qty</th>
                          <th className="text-right p-3 text-sm font-medium text-muted-foreground">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {storeOverview.scratch_stock_items.map((item, idx) => (
                          <tr key={idx} className="border-b border-border/50">
                            <td className="p-3 text-sm">{item.name || item.denomination_name}</td>
                            <td className="p-3 text-right font-mono text-sm">{item.current_stock || 0}</td>
                            <td className="p-3 text-right font-mono text-sm">
                              {formatCurrency((item.current_stock || 0) * (item.price || 0))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-muted/50 font-semibold">
                          <td className="p-3 text-sm">Total</td>
                          <td className="p-3 text-right font-mono text-sm">{storeOverview.scratch_stock_count.toLocaleString()}</td>
                          <td className="p-3 text-right font-mono text-sm">{formatCurrency(storeOverview.scratch_stock_value)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-4">
                    No scratch card stock data
                  </div>
                )}

                <div className={`p-3 rounded-lg ${
                  storeOverview.total_scratch_variance === 0 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Total Variance</span>
                    <span className={`font-mono font-bold ${
                      storeOverview.total_scratch_variance === 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {storeOverview.total_scratch_variance > 0 ? '+' : ''}{storeOverview.total_scratch_variance}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fuel Stock (only for fuel stations) */}
            {storeOverview.store_type === 'fuel_station' && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Fuel className="h-5 w-5 text-primary" />
                    Fuel Stock
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {storeOverview.fuel_stock?.length > 0 ? (
                    <>
                      {/* Fuel summary totals */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Total Stock</p>
                          <p className="text-lg font-bold font-mono text-blue-400">
                            {(storeOverview.fuel_total_stock || 0).toLocaleString()} L
                          </p>
                        </div>
                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Total Cost Value</p>
                          <p className="text-lg font-bold font-mono text-amber-400">
                            {formatCurrency(storeOverview.fuel_total_cost)}
                          </p>
                        </div>
                        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Total Retail Value</p>
                          <p className="text-lg font-bold font-mono text-green-400">
                            {formatCurrency(storeOverview.fuel_total_retail)}
                          </p>
                        </div>
                        <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Avg Margin (ppl)</p>
                          <p className="text-lg font-bold font-mono text-purple-400">
                            {(storeOverview.fuel_average_margin || 0).toFixed(2)}p
                          </p>
                        </div>
                      </div>

                      {/* Fuel stock table */}
                      <div className="border border-border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-muted/30 border-b border-border">
                              <th className="text-left p-3 text-sm font-medium text-muted-foreground">Grade</th>
                              <th className="text-right p-3 text-sm font-medium text-muted-foreground">Stock (L)</th>
                              <th className="text-right p-3 text-sm font-medium text-muted-foreground">Avg Cost (ppl)</th>
                              <th className="text-right p-3 text-sm font-medium text-muted-foreground">Margin (ppl)</th>
                              <th className="text-right p-3 text-sm font-medium text-muted-foreground">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {storeOverview.fuel_stock.map((fuel, idx) => (
                              <tr key={idx} className="border-b border-border/50">
                                <td className="p-3 text-sm font-medium">{fuel.grade_name}</td>
                                <td className="p-3 text-right font-mono text-sm">{(fuel.current_stock || 0).toLocaleString()}</td>
                                <td className="p-3 text-right font-mono text-sm">{(fuel.average_cost || 0).toFixed(2)}p</td>
                                <td className={`p-3 text-right font-mono text-sm ${(fuel.margin || 0) > 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                                  {(fuel.margin || 0).toFixed(2)}p
                                </td>
                                <td className="p-3 text-right font-mono text-sm">
                                  {formatCurrency(fuel.cost_value || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-muted/50 font-semibold">
                              <td className="p-3 text-sm">Total</td>
                              <td className="p-3 text-right font-mono text-sm">{(storeOverview.fuel_total_stock || 0).toLocaleString()}</td>
                              <td className="p-3 text-right font-mono text-sm">-</td>
                              <td className="p-3 text-right font-mono text-sm text-purple-400">{(storeOverview.fuel_average_margin || 0).toFixed(2)}p</td>
                              <td className="p-3 text-right font-mono text-sm">{formatCurrency(storeOverview.fuel_total_cost)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">
                      <Fuel className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No fuel grades configured for this station</p>
                      <p className="text-sm mt-2">Add fuel grades in Store Settings to track fuel stock</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* 10 Week Sales Trend - for convenience stores - show shop sales */}
            {storeOverview.store_type !== 'fuel_station' && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    10 Week Shop Sales Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {storeOverview.sales_trend_10_weeks?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={storeOverview.sales_trend_10_weeks}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis 
                          dataKey="week" 
                          tick={{ fill: '#888', fontSize: 10 }}
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getDate()}/${date.getMonth() + 1}`;
                          }}
                        />
                        <YAxis 
                          tick={{ fill: '#888', fontSize: 10 }}
                          tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1a1a1a', 
                            border: '1px solid #333',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [formatCurrency(value), 'Shop Sales']}
                          labelFormatter={(label) => `Week of ${new Date(label).toLocaleDateString('en-GB')}`}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="shop_sales" 
                          stroke="#10b981" 
                          fill="url(#colorShopSales)"
                          name="Shop Sales"
                        />
                        <defs>
                          <linearGradient id="colorShopSales" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                      <p>No sales data available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Show 10 Week Sales Trend for fuel stations (separate row) - Shop Sales + Fuel Volume */}
          {storeOverview.store_type === 'fuel_station' && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  10 Week Trend - Shop Sales & Fuel Volume
                </CardTitle>
              </CardHeader>
              <CardContent>
                {storeOverview.sales_trend_10_weeks?.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Shop Sales Chart */}
                    <div>
                      <p className="text-sm text-muted-foreground mb-2 text-center">Shop Sales (£)</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={storeOverview.sales_trend_10_weeks}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis 
                            dataKey="week" 
                            tick={{ fill: '#888', fontSize: 9 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getDate()}/${date.getMonth() + 1}`;
                            }}
                          />
                          <YAxis 
                            tick={{ fill: '#888', fontSize: 9 }}
                            tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1a1a1a', 
                              border: '1px solid #333',
                              borderRadius: '8px'
                            }}
                            formatter={(value) => [formatCurrency(value), 'Shop Sales']}
                            labelFormatter={(label) => `Week of ${new Date(label).toLocaleDateString('en-GB')}`}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="shop_sales" 
                            stroke="#10b981" 
                            fill="url(#colorShopSalesFuel)" 
                            name="Shop Sales"
                          />
                          <defs>
                            <linearGradient id="colorShopSalesFuel" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Fuel Volume Chart */}
                    <div>
                      <p className="text-sm text-muted-foreground mb-2 text-center">Fuel Volume (Litres)</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={storeOverview.sales_trend_10_weeks}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis 
                            dataKey="week" 
                            tick={{ fill: '#888', fontSize: 9 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getDate()}/${date.getMonth() + 1}`;
                            }}
                          />
                          <YAxis 
                            tick={{ fill: '#888', fontSize: 9 }}
                            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k L`}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1a1a1a', 
                              border: '1px solid #333',
                              borderRadius: '8px'
                            }}
                            formatter={(value) => [`${value?.toLocaleString() || 0} L`, 'Fuel Volume']}
                            labelFormatter={(label) => `Week of ${new Date(label).toLocaleDateString('en-GB')}`}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="fuel_volume" 
                            stroke="#f97316" 
                            fill="url(#colorFuelVolume)" 
                            name="Fuel Volume"
                          />
                          <defs>
                            <linearGradient id="colorFuelVolume" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                    <p>No sales data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardPage;
